import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LogOut, FileText, Home, Menu, X,
  KeyRound, Inbox, Settings as SettingsIcon, Image, ShieldAlert,
  Users, ArrowLeftRight, BookOpen, Search, Mail, BarChart3,
} from 'lucide-react';
import Seo from './Seo';
import { apiFetch } from '../lib/api';
import { useAdminData, useAdminRefresh } from '../context/AdminPollingContext';

interface NavItem { to: string; label: string; icon: React.ElementType; badge?: number; }
interface NavGroup { label: string; items: NavItem[]; }

interface SearchResult {
  posts: { id: string; title: string; slug: string; lang: string; status: string }[];
  inboxes: { address: string; provider: string; created_at: number }[];
  logs: { id: string; action: string; admin_email: string; entity_type: string; ts: number }[];
}

// ── Activity context — shared across all admin pages ────────────────────────
// Tracks last user activity (click/key/scroll). If inactive for 3 minutes,
// admin is logged out and polling stops. Saves D1 reads + Worker calls.
function getActivityState() {
  // Use module-scoped variables via closure
  let listeners: Array<(active: boolean) => void> = [];
  let lastActivity = Date.now();
  let timer: ReturnType<typeof setInterval> | null = null;
  const ACTIVITY_TIMEOUT_MS = 3 * 60 * 1000;

  function initActivityTracker() {
    if (timer) return;
    const events = ['click', 'keydown', 'scroll', 'mousemove', 'touchstart'];
    const handler = () => { lastActivity = Date.now(); };
    events.forEach(e => document.addEventListener(e, handler, { passive: true }));

    timer = setInterval(() => {
      const active = Date.now() - lastActivity < ACTIVITY_TIMEOUT_MS;
      listeners.forEach(fn => fn(active));
    }, 5_000);

    window.addEventListener('beforeunload', () => {
      events.forEach(e => document.removeEventListener(e, handler));
      if (timer) clearInterval(timer);
    });
  }

  function addListener(fn: (active: boolean) => void) {
    listeners.push(fn);
  }

  function removeListener(fn: (active: boolean) => void) {
    listeners = listeners.filter(l => l !== fn);
  }

  function getActive() {
    return Date.now() - lastActivity < ACTIVITY_TIMEOUT_MS;
  }

  return { initActivityTracker, addListener, removeListener, getActive };
}

// Initialize the activity tracker singleton
const activityTracker = getActivityState();

export function useAdminActivity() {
  // Admin activity is now handled by unified polling context
  return true;
}

function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  // Cmd/Ctrl+K to open
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else { setQ(''); setResults(null); }
  }, [open]);

  const search = useCallback(async (query: string) => {
    if (query.length < 2) { setResults(null); return; }
    setLoading(true);
    const res = await apiFetch(`/api/admin/search?q=${encodeURIComponent(query)}`);
    if (res.ok) setResults(await res.json());
    setLoading(false);
  }, []);

  const handleInput = (val: string) => {
    setQ(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 300);
  };

  const go = (path: string) => { navigate(path); setOpen(false); };

  const hasResults = results && (results.posts.length + results.inboxes.length + results.logs.length) > 0;

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700 transition text-sm"
        title="Search (Ctrl+K)"
      >
        <Search size={14} />
        <span className="hidden sm:inline">Search…</span>
        <kbd className="hidden sm:inline text-[10px] bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded font-mono">⌘K</kbd>
      </button>

      {/* Modal overlay */}
      {open && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh] px-4" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Search input */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <Search size={16} className="text-gray-400 shrink-0" />
              <input
                ref={inputRef}
                value={q}
                onChange={e => handleInput(e.target.value)}
                placeholder="Search posts, inboxes…"
                className="flex-1 bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none"
              />
              {loading && <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin shrink-0" />}
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X size={16} />
              </button>
            </div>

            {/* Results */}
            <div className="max-h-[60vh] overflow-y-auto p-2">
              {q.length >= 2 && !loading && !hasResults && (
                <p className="text-center text-gray-400 text-sm py-8">No results for "{q}"</p>
              )}
              {q.length < 2 && (
                <div className="py-6 text-center text-gray-400 text-xs">
                  <Search size={24} className="mx-auto mb-2 opacity-30" />
                  Type at least 2 characters to search
                </div>
              )}

              {/* Blog posts */}
              {results && results.posts.length > 0 && (
                <div className="mb-2">
                  <p className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">Blog Posts</p>
                  {results.posts.map(p => (
                    <button key={p.id} onClick={() => go(`/blog/edit/${p.id}`)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-blue-50 dark:hover:bg-blue-900/20 text-left transition">
                      <FileText size={14} className="text-blue-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{p.title}</p>
                        <p className="text-xs text-gray-400 font-mono truncate">{p.slug} · {p.lang} · {p.status}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Inboxes */}
              {results && results.inboxes.length > 0 && (
                <div className="mb-2">
                  <p className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">Inboxes</p>
                  {results.inboxes.map(inbox => (
                    <button key={inbox.address} onClick={() => go(`/inboxes/${encodeURIComponent(inbox.address)}`)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-emerald-50 dark:hover:bg-emerald-900/20 text-left transition">
                      <Inbox size={14} className="text-emerald-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-mono text-gray-900 dark:text-gray-100 truncate">{inbox.address}</p>
                        <p className="text-xs text-gray-400">{inbox.provider} · {new Date(inbox.created_at * 1000).toLocaleDateString()}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Audit logs */}
              {results && results.logs?.length > 0 && (
                <div className="mb-2">
                  <p className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">Audit Log</p>
                  {results.logs.map(log => (
                    <button key={log.id} onClick={() => go('/audit-log')}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-purple-50 dark:hover:bg-purple-900/20 text-left transition">
                      <BookOpen size={14} className="text-purple-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{log.action.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-gray-400 truncate">{log.admin_email} · {new Date(log.ts * 1000).toLocaleString()}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer hint */}
            <div className="border-t border-gray-100 dark:border-gray-800 px-4 py-2 flex gap-3 text-[10px] text-gray-400">
              <span><kbd className="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded">↵</kbd> Open</span>
              <span><kbd className="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded">Esc</kbd> Close</span>
              <span><kbd className="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded">⌘K</kbd> Toggle</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const active = useAdminActivity();

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMobileOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Auto-logout after 3 minutes of inactivity
  useEffect(() => {
    if (!active) {
      localStorage.removeItem('admin_token');
      navigate('/login');
    }
  }, [active, navigate]);

  const groups: NavGroup[] = [
    {
      label: 'Overview',
      items: [
        { to: '/',          label: 'Dashboard',    icon: Home },
        { to: '/analytics', label: 'Analytics',    icon: BarChart3 },
      ],
    },
    {
      label: 'Content',
      items: [
        { to: '/blog',   label: 'Blog',   icon: FileText },
        { to: '/media', label: 'Media Library', icon: Image },
      ],
    },
    {
      label: 'Inboxes',
      items: [
        { to: '/inboxes',      label: 'Inbox Manager', icon: Inbox },
        { to: '/own-mail',     label: 'Own Mail',      icon: Mail },
      ],
    },
    {
      label: 'Developer',
      items: [
        { to: '/api-keys',      label: 'API Keys',      icon: KeyRound },
      ],
    },
    {
      label: 'Site',
      items: [
        { to: '/redirects',     label: 'Redirects',     icon: ArrowLeftRight },
        { to: '/abuse-reports', label: 'Abuse Reports', icon: ShieldAlert },
      ],
    },
    {
      label: 'Admin',
      items: [
        { to: '/admin-users', label: 'Admin Users', icon: Users },
        { to: '/settings',    label: 'Settings',    icon: SettingsIcon },
      ],
    },
  ];

  const logout = () => { localStorage.removeItem('admin_token'); window.location.href = '/login'; };
  const isActive = (to: string) => to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  const NavLinks = ({ expanded }: { expanded: boolean }) => (
    <nav className="flex-1 px-2 py-3 overflow-y-auto space-y-1">
      {groups.map(group => (
        <div key={group.label} className="mb-1">
          {expanded && (
            <p className="px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 select-none">
              {group.label}
            </p>
          )}
          {!expanded && <div className="h-px bg-gray-200/60 dark:bg-gray-700/60 mx-2 my-2" />}
          {group.items.map(({ to, label, icon: Icon, badge }) => (
            <Link key={to} to={to}
              className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
                isActive(to)
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md shadow-blue-500/20'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700/60'
              }`}>
              <Icon size={18} className="shrink-0" />
              {expanded && <span className="truncate">{label}</span>}
              {expanded && badge != null && badge > 0 && (
                <span className="ml-auto bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{badge}</span>
              )}
              {!expanded && badge != null && badge > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
              )}
            </Link>
          ))}
        </div>
      ))}
    </nav>
  );

  const SidebarContent = ({ expanded }: { expanded: boolean }) => (
    <div className="flex flex-col h-full">
      <div className={`flex items-center gap-3 px-4 py-4 border-b border-gray-200/60 dark:border-gray-700/60 ${expanded ? '' : 'justify-center'}`}>
        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg shrink-0">
          <span className="text-white text-xs font-bold">iM</span>
        </div>
        {expanded && (
          <div>
            <p className="text-sm font-bold text-gray-900 dark:text-gray-100 leading-none">inbox Me</p>
            <p className="text-[10px] text-gray-400 dark:text-gray-500 leading-none mt-0.5">Admin Panel</p>
          </div>
        )}
      </div>
      <NavLinks expanded={expanded} />
      <div className="p-2 border-t border-gray-200/60 dark:border-gray-700/60">
        <button onClick={logout}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors ${expanded ? '' : 'justify-center'}`}>
          <LogOut size={18} className="shrink-0" />
          {expanded && <span>Logout</span>}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Seo title="Admin — inbox Me" />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 flex">

        {/* Desktop sidebar */}
        <aside className={`hidden md:flex flex-col fixed left-0 top-0 h-full bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl border-r border-gray-200/60 dark:border-gray-700/60 shadow-xl transition-all duration-300 z-30 ${desktopOpen ? 'w-56' : 'w-16'}`}>
          <SidebarContent expanded={desktopOpen} />
          <button onClick={() => setDesktopOpen(o => !o)}
            className="absolute -right-3 top-20 w-6 h-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full flex items-center justify-center shadow hover:shadow-md transition z-10"
            title={desktopOpen ? 'Collapse' : 'Expand'}>
            <span className={`transition-transform duration-300 text-gray-500 text-xs ${desktopOpen ? 'rotate-180' : ''}`}>›</span>
          </button>
        </aside>

        {/* Mobile overlay */}
        {mobileOpen && (
          <div className="fixed inset-0 z-40 md:hidden" onClick={() => setMobileOpen(false)}>
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          </div>
        )}
        <aside className={`fixed left-0 top-0 h-full w-64 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-r border-gray-200/60 dark:border-gray-700/60 shadow-2xl z-50 md:hidden transition-transform duration-300 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <SidebarContent expanded={true} />
        </aside>

        {/* Main content */}
        <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${desktopOpen ? 'md:ml-56' : 'md:ml-16'}`}>
          {/* Top bar */}
          <header className="sticky top-0 z-20 flex items-center gap-3 px-4 py-2.5 bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl border-b border-gray-200/60 dark:border-gray-700/60">
            {/* Mobile menu toggle */}
            <button onClick={() => setMobileOpen(o => !o)} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition md:hidden">
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            {/* Mobile logo */}
            <div className="flex items-center gap-2 md:hidden">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <span className="text-white text-[10px] font-bold">iM</span>
              </div>
              <span className="font-bold text-sm text-gray-900 dark:text-gray-100">inbox Me Admin</span>
            </div>
            {/* Spacer */}
            <div className="flex-1" />
            {/* Global search */}
            <GlobalSearch />
          </header>

          {/* Page content */}
          <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
            <Outlet />
          </main>
        </div>
      </div>
    </>
  );
}
